import React from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { generateColorPalette } from "../utils/enhancedColorUtils"
import { applyButtonTheme } from "../utils/themeUtils"
import { buildEventTypeUrl, buildServiceUrl } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const EventsSectionMobile = ({
  section,
  practice,
  theme,
  highlightedField,
  pageSlug = null,
  defaultPageType = null
}) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "list"
  const showRegistration = settings.showRegistration !== false
  const showDate = settings.showDate !== false
  const showLocation = settings.showLocation !== false
  const registrationButtonText = settings.registrationButtonText || "Details"

  // Get theme colors with fallback generation
  const getThemeColors = () => {
    if (!theme?.colors) return { bgLight: "#FFFFFF", bgMedium: "#E6DAD7", bgExtraLight: "#F2EEEC" }

    const colors = { ...theme.colors }

    if (!colors.bgMedium || !colors.bgExtraLight) {
      const primaryColor = colors.primary || "#BD654F"
      const generated = generateColorPalette(primaryColor)
      colors.bgMedium = colors.bgMedium || generated.bgMedium
      colors.bgExtraLight = colors.bgExtraLight || generated.bgExtraLight
      colors.bgLight = colors.bgLight || generated.bgLight
    }

    return colors
  }

  const themeColors = getThemeColors()
  const sectionBackgroundColor = themeColors.bgLight || "#FFFFFF"

  // Get practice scheduled events (bookableEvents - camelCase)
  const allPracticeEvents = practice?.bookableEvents || []
  const selectedEventIds = settings.selectedEventIds || []

  // Filter for upcoming events only
  const upcomingEvents = allPracticeEvents.filter((event) => {
    if (!event.startsAt) return false
    const eventDate = new Date(event.startsAt)
    const now = new Date()
    return eventDate > now
  })

  // If specific events are selected, use those; otherwise use all upcoming events
  const selectedEvents =
    selectedEventIds.length > 0 ? upcomingEvents.filter((event) => selectedEventIds.includes(event.id)) : upcomingEvents

  // Determine if we're on the home page - use pageSlug if available, otherwise fall back to pathname
  // Determine if we're on the home page (by defaultPageType or slug fallback)
  const isHomePage =
    defaultPageType === "home" ||
    (defaultPageType === null &&
      (pageSlug === "home" ||
        pageSlug === null ||
        window.location.pathname === "/" ||
        window.location.pathname === "/home" ||
        window.location.pathname.endsWith("/home")))

  // Limit based on layout and page
  let maxItems
  if (isHomePage) {
    // On home page, limit to 3 items max
    maxItems = 3
  } else {
    // On other pages, show ALL items
    maxItems = selectedEvents.length
  }

  const events = selectedEvents.slice(0, maxItems)
  const hasMoreEvents = selectedEvents.length > maxItems

  const handleEventRegistration = (event) => {
    // Navigate to the specific event page or event type page
    if (event.bookableEventTypeSlug) {
      const eventUrl = buildEventTypeUrl(event.bookableEventTypeSlug, practice)
      window.location.href = eventUrl
    } else {
      // Fallback to services page
      const servicesUrl = buildServiceUrl(null, practice)
      window.location.href = servicesUrl
    }
  }

  const formatPrice = (amountCents, currency) => {
    if (amountCents === 0) {
      return "Free"
    }
    const amount = amountCents / 100
    return `$${amount.toFixed(2)}${currency === "CAD" ? " CAD" : ""}`
  }

  const formatEventDate = (startsAt, endsAt, timeZone) => {
    if (!startsAt) return ""
    const eventDate = new Date(startsAt)
    const formattedDate = eventDate.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric"
    })
    const formattedTime = eventDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: timeZone
    })
    return `${formattedDate} at ${formattedTime}`
  }

  // List layout - horizontal cards with image on left, content on right
  const renderEventList = (event) => (
    <div key={event.id} className="relative flex flex-col items-center gap-4 border border-[#e6dad7] bg-white p-6">
      <div className="flex w-full flex-col items-center gap-4">
        <div className="w-full">
          {event.photoUrl ? (
            <img
              src={event.photoUrl}
              alt={event.name}
              className="h-32 w-full rounded bg-center bg-no-repeat object-cover"
              style={{ backgroundSize: "155.84% 158.4%" }}
            />
          ) : (
            <div className="flex h-32 w-full items-center justify-center rounded bg-gray-200">
              <span className="text-gray-400">No image</span>
            </div>
          )}
        </div>

        <div className="w-full space-y-2">
          <div>
            {event.amount_cents && (
              <p
                className="text-[16px] font-semibold leading-[24px] text-[#55433f]"
                style={{ fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif" }}>
                {formatPrice(event.amount_cents, event.amount_currency || "USD")}
              </p>
            )}
            <h3
              className="line-clamp-2 text-[20px] font-bold leading-[28px] text-[#454a4f]"
              style={{
                fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden"
              }}>
              {event.name}
            </h3>
          </div>

          {showDate && event.starts_at && (
            <p
              className="whitespace-pre text-[14px] font-normal leading-[20px] text-[#55433f]"
              style={{ fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif" }}>
              {formatEventDate(event.starts_at, event.ends_at, event.time_zone)}
              {showLocation && event.location && ` • ${event.location}`}
            </p>
          )}

          <div
            className="prose prose-lg max-w-none overflow-hidden text-[14px] font-normal leading-[20px] text-[#454a4f]"
            style={{
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical"
            }}
            dangerouslySetInnerHTML={{ __html: event.description }}
          />
        </div>
      </div>

      {showRegistration && (
        <div className="w-full">
          <button
            onClick={() => handleEventRegistration(event)}
            className="self-start px-6 py-2.5 text-[14px] font-normal leading-[20px] text-white transition-all hover:shadow-lg"
            style={applyButtonTheme(
              {
                backgroundColor: theme?.colors?.primary || "#BD654F",
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                borderRadius: theme?.borderRadius || "8px"
              },
              theme
            )}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
            }}>
            {registrationButtonText}
          </button>
        </div>
      )}
    </div>
  )

  // Card layout for 2 and 3 highlights
  const renderEventCard = (event) => (
    <div key={event.id} className="flex flex-col gap-3">
      <div className="aspect-[396/250] w-full">
        {event.photoUrl ? (
          <img
            src={event.photoUrl}
            alt={event.name}
            className="h-full w-full bg-center bg-no-repeat object-cover"
            style={{ backgroundSize: "100% 158.4%" }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gray-200">
            <span className="text-gray-400">No image</span>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <div>
          {event.amount_cents && (
            <p
              className="text-[16px] font-semibold leading-[24px] text-[#55433f]"
              style={{ fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif" }}>
              {formatPrice(event.amount_cents, event.amount_currency || "USD")}
            </p>
          )}
          <h3
            className="line-clamp-2 text-[18px] font-bold leading-[24px] text-[#454a4f]"
            style={{
              fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden"
            }}>
            {event.name}
          </h3>
        </div>

        {showDate && event.starts_at && (
          <p
            className="text-[12px] font-normal leading-[18px] text-[#55433f]"
            style={{ fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif" }}>
            {formatEventDate(event.starts_at, event.ends_at, event.time_zone)}
            {showLocation && event.location && ` • ${event.location}`}
          </p>
        )}

        <div
          className="prose prose-lg max-w-none overflow-hidden text-[14px] font-normal leading-[20px] text-[#454a4f]"
          style={{
            fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical"
          }}
          dangerouslySetInnerHTML={{ __html: event.description }}
        />
      </div>

      {showRegistration && (
        <button
          onClick={() => handleEventRegistration(event)}
          className="self-start px-6 py-2.5 text-[14px] font-normal leading-[20px] text-white transition-all hover:shadow-lg"
          style={applyButtonTheme(
            {
              backgroundColor: theme?.colors?.primary || "#BD654F",
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
              borderRadius: theme?.borderRadius || "8px"
            },
            theme
          )}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
          }}>
          {registrationButtonText}
        </button>
      )}
    </div>
  )

  const renderContent = () => (
    <div className="mx-auto max-w-[1160px]">
      <div className="flex flex-col gap-12">
        <div className="flex flex-col gap-6">
          {section.headline && (
            <div className="mx-2 flex items-center justify-between">
              <h2
                className="text-[28px] font-bold leading-[36px] text-[#55433f]"
                style={{ fontFamily: "Playfair Display, serif" }}>
                {section.headline}
              </h2>
              {hasMoreEvents && isHomePage && (
                <button
                  onClick={() => {
                    const servicesUrl = buildServiceUrl(null, practice, { anchor: "events-section-full" })
                    window.location.href = servicesUrl
                  }}
                  className="px-4 py-1.5 text-xs font-medium text-white transition-colors"
                  style={applyButtonTheme(
                    {
                      backgroundColor: theme?.colors?.primary || "#BD654F",
                      fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                      borderRadius: theme?.borderRadius || "12px"
                    },
                    theme
                  )}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
                  }}>
                  View All
                </button>
              )}
            </div>
          )}

          {events.length > 0 ? (
            <div className="flex flex-col gap-4">
              {layout === "list" && <div className="flex flex-col gap-4">{events.map(renderEventList)}</div>}

              {layout === "2_highlights" && (
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col gap-6">
                    <div className="w-full">{events.slice(0, 1).map(renderEventCard)}</div>
                  </div>
                </div>
              )}

              {layout === "3_highlights" && (
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col gap-6">
                    {events.slice(0, 3).map((event, index) => (
                      <div key={event.id || index} className="w-full">
                        {renderEventCard(event)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">
              <p>No upcoming events found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  const sectionStyle = {
    backgroundColor: sectionBackgroundColor
  }

  return (
    <section
      id="events-section-full"
      className={twMerge("scroll-mt-16 py-10 pb-12", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={sectionStyle}>
      <AnimatedSection>{renderContent()}</AnimatedSection>
    </section>
  )
}

export default EventsSectionMobile
